import { useState } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { CheckCircle2, Clock, XCircle, Ban, Timer, Trash2, Loader2 } from 'lucide-react';

const STATUS = {
  pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-800 border-amber-200', Icon: Clock },
  confirmed: { label: 'Confirmed', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: CheckCircle2 },
  expired: { label: 'Expired', cls: 'bg-red-50 text-red-800 border-red-200', Icon: XCircle },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-600 border-gray-200', Icon: Ban },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const timeLeft = (iso) => {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3.6e6), m = Math.floor((ms % 3.6e6) / 6e4);
  return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
};

export const ReservationRow = ({ r, onAction, busy }) => {
  const [note, setNote] = useState('');
  const s = STATUS[r.status] || STATUS.pending;
  const isBusy = busy === r.id;
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/70 align-top" data-testid={`reservation-row-${r.id}`}>
      <td className="py-3 pr-3">
        <p className="font-mono text-xs font-bold tracking-wider text-wine-800">{r.code}</p>
        <p className="text-[11px] text-gray-400 mt-0.5">{fmt(r.created_at)}</p>
      </td>
      <td className="py-3 pr-3">
        <p className="font-medium text-gray-900">{r.last_name}, {r.first_name}</p>
        <p className="text-xs text-gray-500">{r.email}{r.phone ? ` · ${r.phone}` : ''}</p>
        {(r.roommate_preference || r.notes) && <p className="text-[11px] text-gray-400 mt-1 max-w-xs">{[r.roommate_preference, r.notes].filter(Boolean).join(' · ')}</p>}
      </td>
      <td className="py-3 pr-3">
        <p className="text-gray-800">{r.tier_name}</p>
        <p className="text-xs text-gray-500">${r.amount_confirmed ?? r.price}</p>
      </td>
      <td className="py-3 pr-3">
        <Badge className={`${s.cls} text-xs gap-1`}><s.Icon className="w-3 h-3" />{s.label}</Badge>
        {r.status === 'pending' && <p className="text-[11px] text-gray-500 mt-1">{timeLeft(r.expires_at)}</p>}
        {r.status === 'pending' && r.payment_reported_at && <p className="text-[11px] text-emerald-700 font-medium mt-0.5">Says paid {fmt(r.payment_reported_at)}</p>}
        {r.status === 'confirmed' && <p className="text-[11px] text-gray-500 mt-1">{fmt(r.confirmed_at)}</p>}
      </td>
      <td className="py-3">
        {r.status === 'confirmed' ? (
          <div className="flex gap-1.5 justify-end">
            <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 h-8" disabled={isBusy} onClick={() => onAction('cancel', r, note)} data-testid={`cancel-${r.id}`}>Cancel</Button>
          </div>
        ) : r.status === 'cancelled' ? (
          <div className="flex gap-1.5 justify-end">
            <Button size="sm" variant="ghost" className="text-gray-500 h-8" disabled={isBusy} onClick={() => onAction('delete', r)} data-testid={`delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ) : (
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex gap-1.5">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" disabled={isBusy} onClick={() => onAction('confirm', r, note)} data-testid={`confirm-${r.id}`}>
                {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Confirm ${r.price}</>}
              </Button>
              <Button size="sm" variant="outline" className="h-8" disabled={isBusy} onClick={() => onAction('extend', r)} title="Extend hold" data-testid={`extend-${r.id}`}><Timer className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" disabled={isBusy} onClick={() => onAction('cancel', r, note)} title="Release bed" data-testid={`cancel-${r.id}`}><Ban className="w-3.5 h-3.5" /></Button>
            </div>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (e.g. Venmo 9/21)" className="text-[11px] border rounded px-2 py-1 w-44 focus:outline-none focus:border-wine" data-testid={`note-${r.id}`} />
          </div>
        )}
      </td>
    </tr>
  );
};
